import { JSX } from 'react';
import { LanguagesAbbr, TranslateString } from './translation.types';
import type { EPaletteModes, FontPack, ThemePack } from './theme-packs.types';
import type { EmailBlockContentV2 } from '@/micros/edit-project/grid-areas/main/campaigns/templates/widgets/block-types';
import { UserStatus } from './all-attendees.types';

export interface IScheduledEmailCustomizations {
  [language: string]: IScheduledEmailContent | IScheduledEmailContentV2;
}

export interface IScheduledEmailCustomizationsV2 extends IScheduledEmailCustomizations {
  v2: IScheduledEmailContentV2;
}

export type FontPackCampaignEssentials = Pick<FontPack, 'body_name' | 'body_url' | 'heading_name' | 'heading_url'>;
export type ThemePackCampaignEssentials = Pick<
  ThemePack,
  'color_pack' | 'colors' | 'theme_mode' | 'title' | 'custom_styles'
> & { font_pack: FontPackCampaignEssentials | undefined };
export interface IScheduledEmailContentV2 {
  title: TranslateString;
  description: TranslateString;
  salutation: TranslateString;
  cta: TranslateString;
  startIntro: TranslateString;
  subject: TranslateString;
  headingFont: string;
  bodyFont: string;
  headingImage: string;
  mainImage: string;
  secondaryImage?: string;
  backgroundColor: string;
  containerColor: string;
  accentColor: string;
  headingTextColor: string;
  bodyTextColor: string;
  secondaryAccentColor: string;
  secondaryBackgroundColor: string;
  buttonTextColor: string;
  campaignTitle: string;
  templateId: string;
  sha?: string;
  themeColorPackId?: number;
  // the pack the editor rendered with at its last save, stored whole so the senders render with it
  // without looking it up - an edit to the pack itself reaches the email once the campaign is saved again
  // we don't need to store the ENTIRE theme pack in the db row
  themeColorPack?: ThemePackCampaignEssentials | ThemePack;
  themeMode?: EPaletteModes;
  hideBrandliveLogo?: boolean;
  // ordered content-block instances a template renders instead of its fixed JSX composition, when
  // present (see block-types.ts) - absent for emails/templates not yet converted to this system
  blocks?: EmailBlockContentV2[];
  // note: the 8 color fields above are template-wide; per-block overrides live on each block's own
  // style (EmailBlockContentV2.style) instead, so they reset along with it on a template switch
}

export interface IScheduledEmailContent {
  base: string; // standard email content
  subject: string; // email subject line
  attachments?: IEmailAttachment[]; // email attachments
  eventUrl?: string;
  sessionUrl?: string;
  plainTextEmail?: string;
  button?: string;
  title?: string;
  description?: string;
  headingFont?: string;
  bodyFont?: string;
  ics?: EmailCalendarLinks;
  [key: string]: string | ImageUploaderOnSaveParam | IEmailAttachment[] | EmailCalendarLinks | undefined; // customized content
}

export interface IEmailAttachment {
  name: string;
  content: string;
  contentId: string;
  contentType?: string;
  disposition?: string;
}

export interface IScheduledEmailSettings {
  showStartTime?: boolean;
  showAddCalendar?: boolean;
  usePlainTextEmail?: boolean;
  icsSettings?: ICSSettings;
}

export interface ICSSettings {
  customTitle?: TranslateString;
  customDescription?: TranslateString;
}

export interface ImageUploaderOnSaveParam {
  original: string;
  edited: string;
}

export interface IScheduledEmail extends IInsertScheduledEmailRequest {
  uuid: string;
  audiences: IMultiSelectOption[];
  status: ScheduledEmailStatus;
  remote_job_id: string;
  draft: boolean; // needs to have changes published
  dynamic_audiences?: UserStatus[];
}

export interface IMultiSelectOption {
  label: string;
  value: string;
}

export interface IInsertScheduledEmailRequest {
  event_uuid: string;
  session_uuid?: string /** only used for session level emails */;
  channel: number;
  /** timestamp of delivery date and time. Time is intervals of 5, no greater than 55 */
  delivery_date: number;
  email_type: EmailTypeEnum;
  customizations: IScheduledEmailCustomizations;
  working_customizations?: IScheduledEmailCustomizations | null;
  audiences?: IMultiSelectOption[];
  sent: boolean;
  language?: LanguagesAbbr | null;
  failed_reason?: string | null;
  timezone?: string;
  settings?: IScheduledEmailSettings;
}

export enum ScheduledEmailStatus {
  deleted,
  active,
}

// if adding a type: be sure to include the word 'session' in any session level type and 'event' in any event level type
export enum EmailTypeEnum {
  session_reminder = 'session_reminder',
  session_live = 'session_live',
  session_ended = 'session_ended',
  event_invite = 'event_invite',
  event_registration_email = 'event_registration_email',
}

export interface EmailTextPhrases {
  'subject.session_reminder': string;
  'subject.session_live': string;
  'subject.session_ended': string;
  'subject.event_invite': string;
  'subject.event_registration_email': string;
  salutation: string;
  salutation2: string;
  invited: string;
  registered: string;
  starting: string;
  soon: string;
  tomorrow: string;
  startingIn: string;
  days: string;
  isLive: string;
  isEnded: string;
  eventStart: string;
  SessionDetails: string;
  WatchSession: string;
  RegisterNow: string;
  ExploreEvent: string;
  AddToCalendar: string;
  ClickHereToJoinPeriod: string;
  ClickHereToJoinColon: string;
}

export interface EmailCalendarLinks {
  iCal: string;
  outlook: string;
  google: string;
  content?: string;
  icsUrl?: string;
}

export interface IEditableContent {
  salutation: JSX.Element;
  reminder: JSX.Element;
  sessionName: JSX.Element;
  sessionDescription: JSX.Element;
}

export const EmailTypeDisplayNames: Record<EmailTypeEnum, string> = {
  [EmailTypeEnum.session_reminder]: 'Upcoming reminder',
  [EmailTypeEnum.session_live]: 'Live reminder',
  [EmailTypeEnum.session_ended]: 'Replay reminder',
  [EmailTypeEnum.event_invite]: 'Invite',
  [EmailTypeEnum.event_registration_email]: 'Registration',
};

export enum EInviteStatus {
  added = 'added',
  scheduled = 'scheduled',
  awaiting = 'awaiting',
  registered = 'registered',
}

export type UpsertBccEmailsRow = {
  email: string;
  channel: number;
  enabled: boolean;
};

export type ChannelBccEmail = {
  id: number;
  email: string;
  channel: number;
  created_at: string;
  modified_at: string | null;
  created_by: number;
  modified_by: number | null;
  enabled: boolean;
};
